import { useCallback, useEffect, useMemo, useState } from "react";

import { createAdminClient, type RuntimeConfig } from "./lib/api.js";
import { adminLogout, fetchAdminMe, type AdminUser } from "./lib/admin.js";
import { AdminLoginScreen } from "./routes/AdminLoginScreen.js";
import { AdminShell } from "./routes/AdminShell.js";

/**
 * The portal's state machine, deliberately small — the same shape as the merchant app:
 *
 *   checking  → asking /admin/me whether this browser already has an admin session
 *   signed-out→ the admin login screen
 *   signed-in → the portal
 *
 * NO SESSION IS INFERRED FROM localStorage. The only source of truth is /admin/me, and a
 * 401 anywhere drops straight back to signed-out — a portal that reaches every org must
 * never believe a revoked staff token is still valid.
 */
type Phase = { kind: "checking" } | { kind: "signed-out" } | { kind: "signed-in"; admin: AdminUser };

export function App({ config }: { readonly config: RuntimeConfig }): React.JSX.Element {
  const [phase, setPhase] = useState<Phase>({ kind: "checking" });

  const client = useMemo(
    () =>
      createAdminClient({
        baseURL: config.apiUrl,
        onUnauthenticated: () => setPhase({ kind: "signed-out" }),
      }),
    [config.apiUrl],
  );

  const load = useCallback(async () => {
    try {
      const me = await fetchAdminMe(client);
      setPhase({ kind: "signed-in", admin: me });
    } catch (error) {
      // A 401 has already routed through onUnauthenticated; anything else still means
      // there is no usable admin session to render. The login screen is the honest answer.
      void error;
      setPhase({ kind: "signed-out" });
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  if (phase.kind === "checking") return <div className="text-muted-foreground grid min-h-svh place-items-center text-sm">Loading…</div>;
  if (phase.kind === "signed-out") return <AdminLoginScreen client={client} onSignedIn={() => void load()} />;

  return (
    <AdminShell
      client={client}
      admin={phase.admin}
      appEnv={config.appEnv}
      onSignOut={async () => {
        await adminLogout(client);
        setPhase({ kind: "signed-out" });
      }}
    />
  );
}
